import { z } from "zod";
import { Form1099Box } from "@common/constants/domains/accounting/form1099Box";
import {
    Form1099Warning,
    Track1099FederalIdType,
} from "@common/types/form1099Report";
import { JournalEntrySourceSchema } from "@common/schemas/domains/accounting/journalEntry";
import { filtersSchema } from "@common/types/filters/general";

export const form1099ReportTransactionLineSchema = z.object({
    id: z.string(),
    entityId: z.number().int(),
    dateOnly: z.string(),
    source: JournalEntrySourceSchema,
    recurringRuleId: z.string().nullable(),
    referenceNumber: z.string(),
    description: z.string().nullable(),
    bankDescription: z.string().nullable(),
    form1099Box: z.nativeEnum(Form1099Box),
    accountId: z.string(),
    accountName: z.string(),
    accountCodeText: z.string().nullable(),
    splitLabel: z.string(),
    classIds: z.array(z.string().uuid()),
    amount: z.number(),
    balance: z.number(),
});

export type Form1099ReportTransactionLine = z.infer<
    typeof form1099ReportTransactionLineSchema
>;

export const form1099ReportRowSchema = z.object({
    counterpartyId: z.string().uuid(),
    businessName: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    federalIdType: z.nativeEnum(Track1099FederalIdType),
    street: z.string().nullable(),
    street2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string(),
    email: z.string().nullable(),
    box1: z.string(),
    warnings: z.array(z.nativeEnum(Form1099Warning)),
    transactions: z.array(form1099ReportTransactionLineSchema).default([]),
});

export type Form1099ReportRow = z.infer<typeof form1099ReportRowSchema>;

export const form1099ReportEntitySchema = z.object({
    entityId: z.number().int(),
    entityName: z.string().nullable(),
    undecidedVendorCount: z.number().int().nonnegative(),
    rows: z.array(form1099ReportRowSchema),
});

export type Form1099ReportEntity = z.infer<typeof form1099ReportEntitySchema>;

export const form1099ReportQuerySchema = z.object({
    workspaceId: z.string().uuid(),
    taxYear: z.coerce.number().int().min(2000).max(2100),
    entityId: z.coerce.number().int().optional(),
});

export type Form1099ReportQuery = z.infer<typeof form1099ReportQuerySchema>;

export const form1099ReportFiltersSchema = filtersSchema
    .pick({
        form1099Boxes: true,
        classes: true,
        classFilterType: true,
        coaAccounts: true,
        splitCoaAccounts: true,
        journalEntrySources: true,
        minAmount: true,
        maxAmount: true,
        amountRounded: true,
        amountRoundingLevel: true,
        counterpartiesIds: true,
    })
    .strip();

export type Form1099ReportFilters = z.infer<typeof form1099ReportFiltersSchema>;

export const form1099NecReportQuerySchema = form1099ReportQuerySchema.merge(
    form1099ReportFiltersSchema,
);

export type Form1099NecReportQuery = z.infer<
    typeof form1099NecReportQuerySchema
>;

export const form1099ReportResponseSchema = z.object({
    taxYear: z.number().int(),
    entities: z.array(form1099ReportEntitySchema),
});

export type Form1099ReportResponse = z.infer<
    typeof form1099ReportResponseSchema
>;

export const track1099CsvQuerySchema = form1099ReportQuerySchema.extend({
    entityId: z.coerce.number().int(),
});

export type Track1099CsvQuery = z.infer<typeof track1099CsvQuerySchema>;
